import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ActionButton from "Components/atoms/buttons/action-button";
import LoadingState from "Components/organisms/loading-state";
import HrViewContent from "Components/organisms/hr-dashboard/hr-view-content";
import { HR_DASHBOARD_TABS } from "Components/templates/hr/hr-dashboard";
import { HrGroupBy, HrView } from "models/hr-dashboard";
import FiscalCalendar from "utils/fiscal-calendar";
import { HrDashboardViews } from "slices/human-resources/hr-dashboard/reducer";
import { getHrView } from "slices/human-resources/hr-dashboard/thunk";

const VIEWS = Object.keys(HR_DASHBOARD_TABS) as HrView[];

// Charts animate when they first draw; wait before opening the print dialog
const PRINT_DELAY_MS = 1500;

// Wide month tables fit better in landscape. Each report starts on a new page.
const PRINT_CSS = `
@page { size: landscape; margin: 12mm; }
@media print {
    body { background: #fff !important; }
    .hr-print-section { break-before: page; }
    .hr-print-section:first-of-type { break-before: auto; }
    .hr-print-section table { font-size: 11px; }
}
`;

// Full HR Dashboard report (every tab) for the FY in ?fy= and the grouping in ?groupBy=.
// Opens the print dialog once loaded; "Save as PDF" is in the same dialog.
// Access is enforced by the API (HR_DASHBOARD screen); small groups are hidden the same way as on screen.
const HRDashboardPrintPage = () => {
    document.title = "HR Dashboard Report | IbexIQ";

    const [searchParams] = useSearchParams();
    const fiscalYear = useMemo(() => Number(searchParams.get("fy")) || FiscalCalendar.currentFiscalYear(), [searchParams]);
    const groupBy: HrGroupBy = searchParams.get("groupBy") === "division" ? "division" : "department";

    const [views, setViews] = useState<HrDashboardViews | null>(null);

    useEffect(() => {
        const load = async () => {
            const results = await Promise.all(VIEWS.map(view => getHrView(view, fiscalYear, groupBy)));
            setViews(Object.fromEntries(VIEWS.map((view, i) => [view, results[i]])) as HrDashboardViews);
        };
        load();
    }, [fiscalYear, groupBy]);

    const hasAnyData = !!views && VIEWS.some(v => views[v]);

    useEffect(() => {
        if (!hasAnyData) return;
        const timer = setTimeout(() => window.print(), PRINT_DELAY_MS);
        return () => clearTimeout(timer);
    }, [hasAnyData]);

    if (!views) return <LoadingState title="Loading HR Dashboard" subtitle="Preparing the report…" />;

    if (!hasAnyData) {
        return <div className="p-4">
            <h4>HR Dashboard — FY{fiscalYear}</h4>
            <p className="text-muted">The report could not be loaded. You may not have access to the HR Dashboard.</p>
        </div>;
    }

    return <div className="p-4" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <style>{PRINT_CSS}</style>
        <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
                <h3 className="mb-1">HR Dashboard — FY{fiscalYear}</h3>
                <div className="text-muted small">
                    Printed {new Date().toLocaleString()} · Grouped by {groupBy}
                </div>
            </div>
            <div className="d-print-none">
                <ActionButton onClick={() => window.print()}>Print / Save as PDF <i className="bx bx-printer" /></ActionButton>
            </div>
        </div>
        {VIEWS.map(view => (
            <section key={view} className="hr-print-section mb-5">
                <h4 className="mb-3">{HR_DASHBOARD_TABS[view]}</h4>
                <HrViewContent view={view} views={views} groupBy={groupBy} printMode />
            </section>
        ))}
    </div>;
};

export default HRDashboardPrintPage;
